import gql from 'graphql-tag';
import { useMemo } from 'react';
import * as Urql from 'urql';

type GetUserSettingsQueryVariables = {
  name: string;
};

type GetUserSettingsQuery = {
  userSetting?: { name: string; setting: unknown } | null;
};

const GetUserSettingsDocument = gql`
  query getUserSettings($name: String!) {
    userSetting(name: $name) {
      name
      setting
    }
  }
`;

export const useGetUserSettingsQuery = (settingsName: string) => {
  const context = useMemo(
    () => ({
      url: process.env.NEXT_PUBLIC_UI_USER_SETTINGS_GRAPHQL_ENDPOINT,
    }),
    []
  );

  return Urql.useQuery<GetUserSettingsQuery, GetUserSettingsQueryVariables>({
    query: GetUserSettingsDocument,
    context,
    variables: {
      name: settingsName,
    },
    pause: settingsName === '',
  });
};

export type UserSettingsMutationVariables = {
  setting: unknown;
  name: string;
};

export type CreateUserSettingsMutation = {
  createUserSetting?: UserSettingsMutationVariables | null;
};

export type UpdateUserSettingsMutation = {
  updateUserSetting?: UserSettingsMutationVariables | null;
};

export const CreateUserSettingsDocument = gql`
  mutation createUserSettings($setting: JSON!, $name: String!) {
    createUserSetting(
      userSettingCreateData: {
        setting: $setting
        name: $name
        ownedBy: { name: "my-stihl" }
        permission: { read: true, write: false, delete: false }
      }
    ) {
      name
      setting
    }
  }
`;

export function useCreateUserSettingsMutation(): [
  Urql.UseMutationState<CreateUserSettingsMutation, UserSettingsMutationVariables>,
  (settingName: string, setting: unknown) => Promise<Urql.OperationResult<CreateUserSettingsMutation, UserSettingsMutationVariables>>
] {
  const context = useMemo(
    () => ({
      url: process.env.NEXT_PUBLIC_UI_USER_SETTINGS_GRAPHQL_ENDPOINT,
    }),
    []
  );

  const [_c, create] = Urql.useMutation<CreateUserSettingsMutation, UserSettingsMutationVariables>(CreateUserSettingsDocument);
  return [_c, (settingsName: string, setting: unknown) => create({ name: settingsName, setting }, context)];
}

export const UpdateUserSettingsDocument = gql`
  mutation updateUserSettings($setting: JSON!, $name: String!) {
    updateUserSetting(userSettingUpdateData: { setting: $setting, name: $name, updatedBy: { name: "my-stihl" } }) {
      name
      setting
    }
  }
`;

export function useUpdateUserSettingsMutation(): [
  Urql.UseMutationState<UpdateUserSettingsMutation, UserSettingsMutationVariables>,
  (settingName: string, setting: unknown) => Promise<Urql.OperationResult<UpdateUserSettingsMutation, UserSettingsMutationVariables>>
] {
  const context = useMemo(
    () => ({
      url: process.env.NEXT_PUBLIC_UI_USER_SETTINGS_GRAPHQL_ENDPOINT,
    }),
    []
  );

  const [_u, update] = Urql.useMutation<UpdateUserSettingsMutation, UserSettingsMutationVariables>(UpdateUserSettingsDocument);
  return [_u, (settingsName: string, setting: unknown) => update({ name: settingsName, setting }, context)];
}
